import React from 'react';
import { determineKeycapColor } from '../../../../utils/colorHelpers';
import { keycapRotationTransform } from '../../../../utils/layoutGeometry';
import { keycapFootprintRects } from '../../../../utils/keycapFootprints';

// this component will render the base layer of each keycap in the svg. It includes:
// 1. the shape of the keycap (filled with it's color)

const KeycapBaseLayer = ({
  keycapData,
  totalVerticalSpacing,
  dataComingFromLayoutEditor,
  dataComingFromColorPicker,
  visibleGroup,
  hexColorCode,
  dataComingFromLegendsEditor,
}) => {
  const oneKeycapUnitInMM = 19.05; // shared keycap unit used by the layout preview and footprint shapes

  const renderKeycapFootprint = (
    keycap,
    visibleRowIndex,
    visibleKeycapIndex
  ) => {
    const commonProps = {
      'data-keycap-footprint': 'true',
      'data-row-index': visibleRowIndex,
      'data-keycap-index': visibleKeycapIndex,
      'data-keycap-label': `row ${visibleRowIndex + 1}, keycap ${
        visibleKeycapIndex + 1
      }`,
      fill: 'red',
      opacity: 0,
      pointerEvents: 'none',
    }; // these invisible shapes are used only for overlap detection

    return keycapFootprintRects(keycap, oneKeycapUnitInMM).map(
      (rect, partIndex) => (
        <rect
          key={partIndex}
          {...commonProps}
          data-footprint-part-index={partIndex}
          {...rect}
        />
      )
    ); // one rectangle of the keycap's size, two for the ISO Enter (utils/keycapFootprints.js)
  }; // renders invisible footprint shapes for overlap detection

  const renderKeycapBorder = (
    keycap,
    rowIndex,
    keycapIndex,
    visibleRowIndex,
    visibleKeycapIndex,
    xPosForKeycap,
    yPosForRow,
    xOffset,
    yOffset,
    matrix,
    dPath
  ) => {
    const baseTransform = `${keycapRotationTransform(keycap, oneKeycapUnitInMM)} translate(${xPosForKeycap + xOffset}, ${yPosForRow + yOffset})`; // place the keycap using the same rotation, offsets and row position as the visual keycap

    return (
      <g
        key={`row-${rowIndex}-keycap-${keycapIndex}`}
        transform={baseTransform}
      >
        {dataComingFromLayoutEditor &&
          renderKeycapFootprint(keycap, visibleRowIndex, visibleKeycapIndex)}

        <g transform={matrix}>
          <path
            d={dPath}
            fill={
              dataComingFromLayoutEditor
                ? determineKeycapColor(
                    visibleGroup,
                    keycap.colorGroup,
                    keycap.group,
                    keycap.row
                  )
                : dataComingFromColorPicker
                  ? `#${hexColorCode}`
                  : dataComingFromLegendsEditor
                    ? '#FFFFFF'
                    : keycap.color
            }
          />
        </g>
      </g>
    );
  }; // renders an invisible footprint of the current keycap and it's base layer

  let visibleRowIndex = -1; // counts only rows that contain real keycaps

  return keycapData.map((row, rowIndex) => {
    const rowHasKeycaps = row.some(
      (keycap) => keycap && Object.keys(keycap).length > 0 && !keycap.isSpacer
    ); // check whether this row contains at least one real keycap

    if (rowHasKeycaps) {
      visibleRowIndex += 1; // only real keycap rows should be counted in warning labels
    }

    let visibleKeycapIndex = 0; // counts only real keycaps inside this visible row
    let xPosForKeycap = 0; // Initialize x position for keycaps
    let yPosForRow = 0 + rowIndex * 19.05 + totalVerticalSpacing; // Adjusts yPosForRow to include totalVerticalSpacing accumulated so far (by vertical spacers added to the keycaps)

    return row.map((keycap, keycapIndex) => {
      let matrix = '';
      let dPath = '';
      const xOffset = keycap.xOffset ? keycap.xOffset * 19.05 : 0;
      const yOffset = keycap.yOffset ? keycap.yOffset * 19.05 : 0;

      if (keycap.isIsoEnter) {
        matrix = 'matrix(-0.2645288,0,0,0.26461335,29.630131,-1.4575274)';
        dPath =
          'm 92.4236,79.7682 0.0186,-0.3677 0.0551,-0.3642 0.0902,-0.3573 0.124,-0.3472 0.1564,-0.3335 0.1876,-0.3166 0.2173,-0.2962 0.2457,-0.2725 0.2725,-0.2458 0.2962,-0.2173 0.3166,-0.1875 0.3335,-0.1564 0.3472,-0.124 0.3573,-0.0902 0.3642,-0.0551 0.3677,-0.0187 11.9753,0 0.436,-0.0427 0.421,-0.1247 0.389,-0.2018 0.338,-0.2737 0.274,-0.3387 0.202,-0.3884 0.125,-0.4211 0.042,-0.4366 V 9.47495 l -0.044,-0.45131 -0.129,-0.43518 -0.209,-0.40145 -0.284,-0.35015 -0.35,-0.28327 -0.401,-0.209 -0.435,-0.12934 -0.452,-0.04431 H 7.92767 L 7.47637,7.21525 7.0412,7.34459 6.63975,7.55358 6.28961,7.83685 6.00634,8.18699 5.79734,8.58844 5.66801,9.02361 5.6237,9.47492 V 145.525 l 0.04431,0.451 0.12934,0.436 0.20899,0.401 0.28328,0.35 0.35014,0.283 0.40146,0.209 0.43518,0.13 0.45131,0.044 H 90.1427 l 0.4468,-0.044 0.431,-0.128 0.3975,-0.206 0.3467,-0.281 0.2804,-0.346 0.2067,-0.398 0.1279,-0.431 0.0439,-0.447 z';

        const currentVisibleKeycapIndex = visibleKeycapIndex;
        visibleKeycapIndex += 1; // spacers do not count as visible keycaps

        const isoEnter = renderKeycapBorder(
          keycap,
          rowIndex,
          keycapIndex,
          visibleRowIndex,
          currentVisibleKeycapIndex,
          xPosForKeycap,
          yPosForRow,
          xOffset,
          yOffset,
          matrix,
          dPath
        );

        xPosForKeycap += 28.575;
        return isoEnter;
      } else if (keycap.isConvex) {
        if (keycap.width === 1 && keycap.height === 1) {
          matrix = 'matrix(0,-0.26458308,-0.26457646,0,19.7112,19.711446)';
          dPath =
            'M 5.7996,3.916885 5.61572,3.92675 5.43519,3.95682 5.25919,4.0055 5.08888,4.07161 4.92545,4.15399 4.77006,4.25146 4.6239,4.36285 4.48813,4.48698 4.36394,4.62268 4.25249,4.76878 4.15497,4.92411 4.07254,5.08749 4.00638,5.25775 3.95767,5.43372 3.92758,5.61422 3.917305,5.79809 3.91733,47.0039 v 24.2112 l 0.01144,0.183 0.03103,0.1795 0.04947,0.175 0.06673,0.1692 0.08284,0.1623 0.09777,0.1542 0.11155,0.145 0.12415,0.1347 0.1356,0.1231 0.14587,0.1105 0.15499,0.0966 0.16294,0.0816 0.16972,0.0656 0.17534,0.0482 0.17979,0.0298 0.18308,0.0097 H 71.2004 l 0.1831,-0.0097 0.1798,-0.0298 0.1753,-0.0483 0.1697,-0.0655 0.163,-0.0816 0.1549,-0.0967 0.1459,-0.1104 0.1356,-0.1231 0.1242,-0.1347 0.1115,-0.145 0.0978,-0.1542 0.0828,-0.1623 0.0667,-0.1692 0.0495,-0.175 0.031,-0.1795 0.0115,-0.183 V 47.0039 5.79809 L 73.0724,5.61422 73.0423,5.43371 72.9936,5.25774 72.9275,5.08748 72.845,4.92409 72.7475,4.76876 72.636,4.62266 72.5118,4.48696 72.3761,4.36283 72.2299,4.25145 72.0745,4.15398 71.9111,4.0716 71.7408,4.00549 71.5648,3.95681 71.3842,3.92674 71.2004,3.916885 Z';

          const currentVisibleKeycapIndex = visibleKeycapIndex;
          visibleKeycapIndex += 1; // spacers do not count as visible keycaps

          const oneUnitConvex = renderKeycapBorder(
            keycap,
            rowIndex,
            keycapIndex,
            visibleRowIndex,
            currentVisibleKeycapIndex,
            xPosForKeycap,
            yPosForRow,
            xOffset,
            yOffset,
            matrix,
            dPath
          );

          xPosForKeycap += keycap.width * 19.05;
          return oneUnitConvex;
        } else if (keycap.width === 1.25 && keycap.height === 1) {
          matrix = 'matrix(0,-0.26458236,-0.26457792,0,24.473453,19.711419)';
          dPath =
            'm 71.2005,90.58705 0.1839,-0.0099 0.1805,-0.0301 0.3463,-0.1148 0.3188,-0.1798 0.282,-0.2355 0.2356,-0.2818 0.18,-0.3187 0.1148,-0.3463 0.0301,-0.1805 0.0103,-0.1839 V 47.5 6.29415 L 73.0725,6.11028 73.0424,5.92977 72.9275,5.58354 72.7476,5.26483 72.5119,4.98302 72.23,4.74751 71.9112,4.56766 71.5649,4.45287 71.3843,4.4228 71.2004,4.412955 H 5.79968 L 5.61581,4.42281 5.43528,4.45288 5.08897,4.56768 4.77015,4.74752 4.48822,4.98304 4.25258,5.26484 4.07263,5.58355 3.95776,5.92978 3.92767,6.11029 3.917395,6.29415 3.91742,47.5 l -2.5e-5,41.2058 0.010275,0.1839 0.03009,0.1805 0.11487,0.3463 0.17996,0.3187 0.23565,0.2818 0.28194,0.2355 0.31883,0.1798 0.34631,0.1148 0.18053,0.0301 0.18388,0.0099 z';

          const currentVisibleKeycapIndex = visibleKeycapIndex;
          visibleKeycapIndex += 1; // spacers do not count as visible keycaps

          const onePointTwoFiveUnitConvex = renderKeycapBorder(
            keycap,
            rowIndex,
            keycapIndex,
            visibleRowIndex,
            currentVisibleKeycapIndex,
            xPosForKeycap,
            yPosForRow,
            xOffset,
            yOffset,
            matrix,
            dPath
          );

          xPosForKeycap += keycap.width * 19.05;
          return onePointTwoFiveUnitConvex;
        } else if (keycap.width === 1.75 && keycap.height === 1) {
          matrix = 'matrix(0,-0.26458236,-0.26458021,0,34.395334,19.711419)';
          dPath =
            'm 71.2005,127.4725 0.3644,-0.0395 0.3463,-0.115 0.3188,-0.18 0.282,-0.235 0.2356,-0.282 0.18,-0.319 0.1148,-0.346 0.0404,-0.364 V 67 8.40833 L 73.0424,8.04395 72.9275,7.69771 72.7476,7.379 72.5119,7.0972 72.23,6.86168 71.9112,6.68184 71.5649,6.56705 71.2004,6.527125 H 5.79968 L 5.43528,6.56705 5.08897,6.68185 4.77015,6.8617 4.48822,7.09721 4.25258,7.37901 4.07263,7.69772 3.95776,8.04395 3.917395,8.40833 3.91742,67 l -2.5e-5,58.592 0.040365,0.364 0.11487,0.346 0.17996,0.319 0.23565,0.282 0.28194,0.235 0.31883,0.18 0.34631,0.115 0.36441,0.0395 z';

          const currentVisibleKeycapIndex = visibleKeycapIndex;
          visibleKeycapIndex += 1; // spacers do not count as visible keycaps

          const onePointSevenFiveUnitConvex = renderKeycapBorder(
            keycap,
            rowIndex,
            keycapIndex,
            visibleRowIndex,
            currentVisibleKeycapIndex,
            xPosForKeycap,
            yPosForRow,
            xOffset,
            yOffset,
            matrix,
            dPath
          );

          xPosForKeycap += keycap.width * 19.05;
          return onePointSevenFiveUnitConvex;
        } else if (keycap.width === 2 && keycap.height === 1) {
          matrix = 'matrix(0,-0.26458236,-0.26458029,0,39.422664,19.711419)';
          dPath =
            'm 71.2005,146.9215 0.3644,-0.0395 0.3463,-0.115 0.3188,-0.18 0.282,-0.235 0.2356,-0.282 0.18,-0.319 0.1148,-0.346 0.0404,-0.365 0,-68.04 V 8.95951 L 73.0424,8.59513 72.9275,8.24889 72.7476,7.93018 72.5119,7.64838 72.23,7.41286 71.9112,7.23302 71.5649,7.11823 71.2004,7.078305 H 5.79968 L 5.43528,7.11824 5.08897,7.23303 4.77015,7.41288 4.48822,7.64839 4.25258,7.9302 4.07263,8.2489 3.95776,8.59513 3.917395,8.95951 3.91742,77 l -2.5e-5,68.04 0.040365,0.365 0.11487,0.346 0.17996,0.319 0.23565,0.282 0.28194,0.235 0.31883,0.18 0.34631,0.115 0.36441,0.0395 z';

          const currentVisibleKeycapIndex = visibleKeycapIndex;
          visibleKeycapIndex += 1; // spacers do not count as visible keycaps

          const twoUnitConvex = renderKeycapBorder(
            keycap,
            rowIndex,
            keycapIndex,
            visibleRowIndex,
            currentVisibleKeycapIndex,
            xPosForKeycap,
            yPosForRow,
            xOffset,
            yOffset,
            matrix,
            dPath
          );

          xPosForKeycap += keycap.width * 19.05;
          return twoUnitConvex;
        } else if (keycap.width === 2.25 && keycap.height === 1) {
          matrix = 'matrix(0,-0.26458236,-0.26458035,0,44.581787,19.711419)';
          dPath =
            'm 71.2005,166.8705 0.3644,-0.0395 0.3463,-0.115 0.3188,-0.18 0.282,-0.236 0.2356,-0.281 0.18,-0.319 0.1148,-0.346 0.0404,-0.365 0,-77.489 V 10.0107 L 73.0424,9.64631 72.9275,9.30007 72.7476,8.98136 72.5119,8.69956 72.23,8.46404 71.9112,8.2842 71.5649,8.16941 71.2004,8.129485 H 5.79968 L 5.43528,8.16942 5.08897,8.28421 4.77015,8.46406 4.48822,8.69957 4.25258,8.98138 4.07263,9.30008 3.95776,9.64632 3.917395,10.0107 3.91742,87.5 l -2.5e-5,77.489 0.040365,0.365 0.11487,0.346 0.17996,0.319 0.23565,0.281 0.28194,0.236 0.31883,0.18 0.34631,0.115 0.36441,0.0395 z';

          const currentVisibleKeycapIndex = visibleKeycapIndex;
          visibleKeycapIndex += 1; // spacers do not count as visible keycaps

          const twoPointTwoFiveUnitConvex = renderKeycapBorder(
            keycap,
            rowIndex,
            keycapIndex,
            visibleRowIndex,
            currentVisibleKeycapIndex,
            xPosForKeycap,
            yPosForRow,
            xOffset,
            yOffset,
            matrix,
            dPath
          );

          xPosForKeycap += keycap.width * 19.05;
          return twoPointTwoFiveUnitConvex;
        } else if (keycap.width === 2.75 && keycap.height === 1) {
          matrix = 'matrix(0,-0.26458236,-0.26458073,0,54.371874,19.711419)';
          dPath =
            'm 71.2005,202.8785 0.3644,-0.0405 0.3463,-0.114 0.3188,-0.18 0.282,-0.236 0.2356,-0.282 0.18,-0.318 0.1148,-0.346 0.0404,-0.365 V 106.5 12.0028 L 73.0424,11.6384 72.9275,11.2922 72.7476,10.9735 72.5119,10.6917 72.23,10.4562 71.9112,10.2763 71.5649,10.1615 71.2004,10.1216 H 5.79968 l -0.3644,0.0399 -0.34631,0.1148 -0.31882,0.1799 -0.28193,0.2355 -0.23564,0.2818 -0.17995,0.3187 -0.11487,0.3462 -0.040365,0.3644 2.5e-5,94.4972 -2.5e-5,94.497 0.040365,0.365 0.11487,0.346 0.17996,0.319 0.23565,0.281 0.28194,0.236 0.31883,0.18 0.34631,0.114 0.36441,0.0405 z';

          const currentVisibleKeycapIndex = visibleKeycapIndex;
          visibleKeycapIndex += 1; // spacers do not count as visible keycaps

          const twoPointSevenFiveUnitConvex = renderKeycapBorder(
            keycap,
            rowIndex,
            keycapIndex,
            visibleRowIndex,
            currentVisibleKeycapIndex,
            xPosForKeycap,
            yPosForRow,
            xOffset,
            yOffset,
            matrix,
            dPath
          );

          xPosForKeycap += keycap.width * 19.05;
          return twoPointSevenFiveUnitConvex;
        } else if (keycap.width === 3 && keycap.height === 1) {
          matrix = 'matrix(0,-0.26458255,-0.26458076,0,59.398712,19.711433)';
          dPath =
            'm 71.2004,222.3275 0.3644,-0.0405 0.3464,-0.115 0.3188,-0.179 0.2819,-0.236 0.2356,-0.282 0.18,-0.318 0.1149,-0.347 0.04035,-0.364 -5e-5,-103.946 5e-5,-103.946 -0.04035,-0.3644 -0.1149,-0.3462 -0.18,-0.3187 L 72.5119,11.2429 72.23,11.0074 71.9111,10.8275 71.5648,10.7127 71.2004,10.6728 H 5.79964 L 5.43523,10.7127 5.08892,10.8275 4.7701,11.0074 4.48818,11.2429 4.25253,11.5247 4.07258,11.8434 3.95771,12.1896 3.917345,12.554 3.91737,116.5 l -2.5e-5,103.946 0.040365,0.364 0.11488,0.347 0.17996,0.318 0.23564,0.282 0.28194,0.236 0.31883,0.179 0.34632,0.115 0.36441,0.0405 z';

          const currentVisibleKeycapIndex = visibleKeycapIndex;
          visibleKeycapIndex += 1; // spacers do not count as visible keycaps

          const threeUnitConvex = renderKeycapBorder(
            keycap,
            rowIndex,
            keycapIndex,
            visibleRowIndex,
            currentVisibleKeycapIndex,
            xPosForKeycap,
            yPosForRow,
            xOffset,
            yOffset,
            matrix,
            dPath
          );

          xPosForKeycap += keycap.width * 19.05;
          return threeUnitConvex;
        } else if (keycap.width === 6.25 && keycap.height === 1) {
          matrix = 'matrix(-0.26457536,0,0,0.26458224,124.35201,-0.66141133)';
          dPath =
            'm 467.295,71.9112 -0.415,0.6008 -0.6,0.4156 -0.711,0.1552 H 24.431 L 23.7204,72.9276 23.1199,72.512 22.7046,71.9112 22.54985,71.2005 V 5.79969 l 0.15475,-0.7107 0.4153,-0.60074 0.6005,-0.41559 0.7106,-0.155375 441.138,2.85e-4 0.711,0.1551 0.6,0.41561 0.415,0.60076 0.1555,0.71072 0,65.40074 z';

          const currentVisibleKeycapIndex = visibleKeycapIndex;
          visibleKeycapIndex += 1; // spacers do not count as visible keycaps

          const sixPointTwoFiveUnitConvex = renderKeycapBorder(
            keycap,
            rowIndex,
            keycapIndex,
            visibleRowIndex,
            currentVisibleKeycapIndex,
            xPosForKeycap,
            yPosForRow,
            xOffset,
            yOffset,
            matrix,
            dPath
          );

          xPosForKeycap += keycap.width * 19.05;
          return sixPointTwoFiveUnitConvex;
        } else if (keycap.width === 7 && keycap.height === 1) {
          matrix = 'matrix(0,-0.26455807,-0.26457891,0,139.43422,19.710491)';
          dPath =
            'm 71.2125,25.1696 0.7074,0.1537 0.5979,0.4133 0.4137,0.5976 0.1544,0.7072 -10e-5,247.9586 10e-5,247.959 -0.1544,0.707 -0.4137,0.597 -0.5979,0.414 -0.7074,0.1535 H 5.78755 l -0.7074,-0.1535 -0.59797,-0.414 -0.41366,-0.597 -0.1544,-0.707 6e-5,-247.959 -6e-5,-247.9586 0.1544,-0.7072 0.41366,-0.5976 0.59797,-0.4133 0.7074,-0.1537 z';

          const currentVisibleKeycapIndex = visibleKeycapIndex;
          visibleKeycapIndex += 1; // spacers do not count as visible keycaps

          const sevenUnitConvex = renderKeycapBorder(
            keycap,
            rowIndex,
            keycapIndex,
            visibleRowIndex,
            currentVisibleKeycapIndex,
            xPosForKeycap,
            yPosForRow,
            xOffset,
            yOffset,
            matrix,
            dPath
          );

          xPosForKeycap += keycap.width * 19.05;
          return sevenUnitConvex;
        }
      } else if (keycap.stepped) {
        matrix = 'matrix(-0.26458929,0,0,0.26469242,34.660588,-0.79799947)';
        dPath =
          'm 129.266,15.6569 0.063,7.7808 0.06,7.7809 0.027,7.781 -0.027,7.7812 -0.06,7.7811 -0.063,7.7811 -0.032,7.7811 -0.015,0.3403 -0.051,0.3372 -0.083,0.331 -0.115,0.3214 -0.145,0.3086 -0.173,0.2927 -0.201,0.2734 -0.228,0.2511 -0.253,0.2257 -0.275,0.199 -0.294,0.1713 -0.309,0.1425 -0.322,0.1128 -0.332,0.0819 -0.337,0.0501 -0.341,0.01425 -7.244,0.02885 -7.245,0.0672 -14.488,0.1919 -14.4882,0.1867 -7.244,0.0606 -7.244,0.0225 -7.244,-0.0238 -7.244,-0.0618 L 38.8307,73.8613 24.3423,73.6697 17.0979,73.6037 9.85339,73.57415 9.55251,73.5594 9.2541,73.5161 8.96097,73.445 8.67595,73.347 8.40186,73.2229 8.14151,73.0733 7.89774,72.8992 7.67336,72.7013 7.47091,72.4806 7.29178,72.2399 7.1371,71.9819 7.00799,71.7095 6.90556,71.4257 6.83092,71.1334 6.7852,70.8354 6.76961,70.5346 6.73633,62.6509 6.67304,54.7673 6.61169,46.8837 6.58412,39 6.61169,31.1163 6.67304,23.2327 6.73633,15.3491 6.76961,7.46538 6.78522,7.16449 6.83098,6.86635 6.90567,6.57388 7.00819,6.28996 7.13741,6.01752 7.29221,5.75944 7.47147,5.51864 7.67408,5.29802 7.89862,5.10014 8.14257,4.9261 8.40308,4.77668 8.67735,4.65266 8.96254,4.55479 9.25583,4.48385 9.55439,4.4406 9.85541,4.425865 17.0998,4.39625 24.344,4.33023 38.8322,4.13871 53.3201,3.95052 60.564,3.88867 l 7.2438,-0.02381 7.2439,0.0225 7.2439,0.06066 14.4879,0.18664 14.4885,0.1919 7.244,0.06722 7.244,0.02883 0.341,0.01433 0.337,0.05007 0.332,0.08192 0.322,0.11275 0.309,0.14253 0.294,0.17129 0.275,0.19902 0.253,0.22572 0.228,0.25102 0.201,0.27346 0.173,0.29267 0.145,0.30865 0.115,0.3214 0.083,0.33092 0.051,0.33721 0.015,0.340285 z';

        const currentVisibleKeycapIndex = visibleKeycapIndex;
        visibleKeycapIndex += 1; // spacers do not count as visible keycaps

        const onePointSevenFiveUnitStepped = renderKeycapBorder(
          keycap,
          rowIndex,
          keycapIndex,
          visibleRowIndex,
          currentVisibleKeycapIndex,
          xPosForKeycap,
          yPosForRow,
          xOffset,
          yOffset,
          matrix,
          dPath
        );

        xPosForKeycap += keycap.width * 19.05;
        return onePointSevenFiveUnitStepped;
      } else if (keycap.hasHomingBar) {
        matrix = 'matrix(-0.26459382,0,0,0.26459382,19.711868,-0.66186907)';
        dPath =
          'm 3.88746,13.9987 -0.0641,8.1672 -0.06433,8.167 -0.02928,8.1671 0.02928,8.1671 0.06433,8.167 0.0641,8.1672 0.02859,8.1672 0.01044,0.1871 0.03062,0.1836 0.04959,0.1791 0.06735,0.1733 0.08393,0.1663 0.0993,0.1581 0.11346,0.1487 0.12644,0.1382 0.13821,0.1264 0.14879,0.1134 0.15815,0.0993 0.16634,0.0839 0.17331,0.0673 0.17909,0.0496 0.18366,0.0307 0.18705,0.0104 8.16722,0.0286 8.1671,0.0641 8.167,0.0644 8.167,0.0293 8.167,-0.0293 8.1671,-0.0644 8.1671,-0.0641 8.1672,-0.0286 0.187,-0.0104 0.1836,-0.0306 0.1791,-0.0496 0.1733,-0.0674 0.1663,-0.0839 0.1581,-0.0993 0.1488,-0.1134 0.1382,-0.1264 0.1264,-0.1382 0.1134,-0.1488 0.0993,-0.1581 0.0839,-0.1663 0.0674,-0.1733 0.0496,-0.1791 0.0306,-0.1836 0.0104,-0.187 0.0286,-8.1673 0.0641,-8.1671 L 73.241,46.667 73.2703,38.5 73.241,30.333 73.1766,22.1659 73.1125,13.9988 73.0839,5.83155 73.0735,5.64452 73.0429,5.46087 72.9933,5.2818 72.9259,5.10851 72.842,4.9422 72.7427,4.78407 72.6293,4.63531 72.5029,4.49712 72.3647,4.37071 72.2159,4.25726 72.0578,4.15798 71.8915,4.07407 71.7182,4.00673 71.5391,3.95715 71.3555,3.92653 71.1685,3.91607 63.0013,3.88746 54.8342,3.82336 46.6671,3.75903 38.5001,3.72975 30.3331,3.75903 22.1661,3.82336 13.999,3.88746 5.83177,3.91607 5.64473,3.92655 5.46106,3.95717 5.28198,4.00676 5.10867,4.0741 4.94233,4.15801 4.78417,4.25727 4.63539,4.3707 4.49718,4.4971 4.37074,4.63527 4.25727,4.78402 4.15798,4.94214 4.07405,5.10844 4.0067,5.28172 3.95711,5.46078 3.92649,5.64443 3.91605,5.83147 Z';

        const currentVisibleKeycapIndex = visibleKeycapIndex;
        visibleKeycapIndex += 1; // spacers do not count as visible keycaps

        const oneUnitHomingBar = renderKeycapBorder(
          keycap,
          rowIndex,
          keycapIndex,
          visibleRowIndex,
          currentVisibleKeycapIndex,
          xPosForKeycap,
          yPosForRow,
          xOffset,
          yOffset,
          matrix,
          dPath
        );

        xPosForKeycap += keycap.width * 19.05;
        return oneUnitHomingBar;
      } else if (!keycap.isSpacer) {
        // if the object is not a spacer (in other words the object represents a keycap):
        if (keycap.width === 1 && keycap.height === 1) {
          matrix = 'matrix(-0.26457944,0,0,0.26457944,19.710815,-0.66181593)';
          dPath =
            'M 73.0735,5.64478 73.0428,5.46111 72.9933,5.28202 72.9259,5.10871 72.842,4.94237 72.7428,4.78421 72.6293,4.63542 72.5029,4.49721 72.3648,4.37076 72.216,4.25729 72.0579,4.15799 71.8916,4.07407 71.7183,4.00671 71.5392,3.95711 71.3556,3.92649 71.1685,3.91605 63.0013,3.88746 54.8342,3.82336 46.6671,3.75903 38.5,3.72975 30.3329,3.75903 22.1658,3.82336 13.9987,3.88746 5.83145,3.91605 5.64441,3.92649 5.46076,3.95711 5.28169,4.00671 5.10841,4.07407 4.94211,4.15799 4.78399,4.25729 4.63525,4.37076 4.49708,4.49721 4.37068,4.63542 4.25725,4.78421 4.15799,4.94237 4.07408,5.10871 4.00675,5.28202 3.95716,5.46112 3.92654,5.64478 3.91607,5.831805 3.88743,14.0042 l -0.06416,8.1722 -0.06434,8.1723 -0.02918,8.1724 0.02938,8.1619 0.06432,8.1618 0.06404,8.1617 0.02858,8.162 0.01046,0.187 0.03062,0.1836 0.04958,0.1791 0.06734,0.1733 0.08391,0.1663 0.09928,0.1581 0.11345,0.1488 0.12641,0.1382 0.13819,0.1264 0.14876,0.1134 0.15813,0.0993 0.16631,0.0839 0.17329,0.0674 0.17907,0.0496 0.18365,0.0306 0.18703,0.0104 8.17245,0.0287 8.1723,0.0641 8.1723,0.0644 8.1725,0.0292 8.1619,-0.0294 8.1618,-0.0643 8.1617,-0.0641 8.162,-0.0286 0.187,-0.0104 0.1836,-0.0306 0.1791,-0.0496 0.1733,-0.0674 0.1663,-0.0839 0.1581,-0.0993 0.1488,-0.1134 0.1382,-0.1264 0.1264,-0.1382 0.1134,-0.1488 0.0993,-0.1581 0.0839,-0.1663 0.0674,-0.1733 0.0496,-0.1791 0.0306,-0.1836 0.0104,-0.187 0.0287,-8.1725 0.0641,-8.1723 0.0644,-8.1723 0.0292,-8.1725 -0.0294,-8.1618 -0.0643,-8.1618 -0.0641,-8.1617 -0.0286,-8.1618 z';

          const currentVisibleKeycapIndex = visibleKeycapIndex;
          visibleKeycapIndex += 1; // spacers do not count as visible keycaps

          const oneUnit = renderKeycapBorder(
            keycap,
            rowIndex,
            keycapIndex,
            visibleRowIndex,
            currentVisibleKeycapIndex,
            xPosForKeycap,
            yPosForRow,
            xOffset,
            yOffset,
            matrix,
            dPath
          );

          xPosForKeycap += keycap.width * 19.05;
          return oneUnit;
        } else if (keycap.width === 1.5 && keycap.height === 1) {
          matrix = 'matrix(-0.26459038,0,0,0.2646046,29.633741,-0.66227219)';
          dPath =
            'm 108.987,73.0429 0.353,-0.1165 0.326,-0.1826 0.288,-0.2392 0.24,-0.2862 0.184,-0.324 0.118,-0.352 0.041,-0.3706 0.028,-8.1681 0.064,-8.1679 0.065,-8.1679 0.029,-8.1679 -0.029,-8.1679 -0.065,-8.1679 -0.064,-8.1679 L 110.537,5.8282 110.496,5.45754 110.378,5.1055 110.194,4.78158 109.953,4.49528 109.666,4.2561 109.34,4.07352 108.987,3.95704 108.616,3.91616 102.289,3.90065 95.962,3.85854 83.3079,3.72988 70.654,3.60087 64.327,3.55831 58.0001,3.54216 51.6732,3.55832 45.3463,3.60088 32.6923,3.72991 20.0383,3.85857 13.7111,3.90066 7.38395,3.91616 7.0129,3.95711 6.65987,4.0736 6.33455,4.25616 6.04664,4.49531 5.80584,4.78155 5.62186,5.10542 5.50438,5.45742 5.4631,5.828085 5.4348,13.9961 5.37073,22.164 5.30633,30.332 5.27699,38.5 l 0.02934,8.168 0.0644,8.168 0.06407,8.1679 0.0283,8.168 0.04128,0.3707 0.11749,0.352 0.184,0.3239 0.24082,0.2862 0.28793,0.2392 0.32534,0.1825 0.35305,0.1165 0.371045,0.0409 6.327145,0.0156 6.327,0.0421 12.6538,0.1286 12.6539,0.129 6.327,0.0426 6.3271,0.0161 6.3272,-0.0161 6.3271,-0.0426 12.6539,-0.129 12.654,-0.1287 6.3268,-0.0421 6.3275,-0.0155 z';

          const currentVisibleKeycapIndex = visibleKeycapIndex;
          visibleKeycapIndex += 1; // spacers do not count as visible keycaps

          const onePointFiveUnit = renderKeycapBorder(
            keycap,
            rowIndex,
            keycapIndex,
            visibleRowIndex,
            currentVisibleKeycapIndex,
            xPosForKeycap,
            yPosForRow,
            xOffset,
            yOffset,
            matrix,
            dPath
          );

          xPosForKeycap += keycap.width * 19.05;
          return onePointFiveUnit;
        } else if (keycap.width === 1.25 && keycap.height === 1) {
          matrix = 'matrix(-0.26459136,0,0,0.26460324,24.738684,-0.66222512)';
          dPath =
            'm 92.1828,13.9965 0.0641,8.1679 0.0646,8.1678 0.0297,8.1678 -0.0292,8.1677 -0.0643,8.1678 -0.0642,8.1679 -0.02855,8.1679 -0.01045,0.1869 -0.0307,0.1835 -0.1171,0.3519 -0.1834,0.3239 -0.2401,0.2865 -0.2872,0.2393 -0.3245,0.1829 -0.3524,0.1167 -0.1836,0.0305 -0.187,0.0105 -5.2127,0.0157 -5.2126,0.0421 -10.4249,0.1285 -10.4249,0.1289 -5.2125,0.0426 L 48.5381,73.458 43.316,73.4421 38.094,73.3996 27.6503,73.2707 17.2066,73.1419 11.9846,73.0996 6.762445,73.0839 6.57542,73.0735 6.39175,73.0429 6.03921,72.9263 5.71445,72.7435 5.42712,72.5042 5.18686,72.2178 5.00333,71.8938 4.88616,71.5419 4.85548,71.3584 4.845025,71.1714 4.81649,63.0035 4.75233,54.8356 4.68797,46.6678 4.65882,38.5 4.68845,30.3322 4.75311,22.1644 4.81717,13.9965 4.845025,5.82856 4.85548,5.64166 4.88614,5.4582 5.00325,5.10631 5.18671,4.78241 5.42686,4.49603 5.71408,4.25669 6.03872,4.0739 6.39114,3.95718 6.57476,3.92658 6.76171,3.916065 11.9768,3.90042 17.1917,3.85827 27.6213,3.7297 38.0509,3.60079 l 5.215,-0.04256 5.215,-0.01623 5.2198,0.01604 5.2197,0.04246 10.439,0.12894 10.4391,0.12873 5.2196,0.04222 5.2198,0.015675 0.187,0.010435 0.1836,0.03054 0.3525,0.11664 0.3247,0.18277 0.2873,0.23938 0.2402,0.28643 0.1834,0.32397 0.1172,0.35196 0.0307,0.18349 0.01045,0.18693 z';

          const currentVisibleKeycapIndex = visibleKeycapIndex;
          visibleKeycapIndex += 1; // spacers do not count as visible keycaps

          const onePointTwoFiveUnit = renderKeycapBorder(
            keycap,
            rowIndex,
            keycapIndex,
            visibleRowIndex,
            currentVisibleKeycapIndex,
            xPosForKeycap,
            yPosForRow,
            xOffset,
            yOffset,
            matrix,
            dPath
          );

          xPosForKeycap += keycap.width * 19.05;
          return onePointTwoFiveUnit;
        } else if (keycap.width === 1.75 && keycap.height === 1) {
          matrix = 'matrix(-0.26458929,0,0,0.26461492,34.660588,-0.79497962)';
          dPath =
            'm 127.676,73.5428 0.353,-0.116 0.326,-0.1818 0.289,-0.2382 0.242,-0.2853 0.184,-0.3231 0.118,-0.3514 0.042,-0.3703 0.028,-8.1688 0.064,-8.1687 0.064,-8.1686 0.03,-8.1687 -0.029,-8.1686 -0.065,-8.1686 L 129.258,14.496 129.23,6.3272 129.188,5.95658 129.071,5.60467 128.887,5.28093 128.646,4.99484 128.358,4.75585 128.032,4.57344 127.679,4.45707 127.308,4.4162 119.894,4.39261 112.481,4.32925 97.6536,4.13612 82.8269,3.94262 75.4137,3.87881 68.0004,3.85458 60.5872,3.87877 53.1739,3.94255 38.3472,4.136 23.5203,4.32914 16.1066,4.39254 8.69288,4.41619 8.32168,4.45705 7.9684,4.57339 7.64277,4.75576 7.35453,4.99469 7.11341,5.28075 6.92915,5.60447 6.81148,5.9564 6.770155,6.32709 6.74189,14.4959 l -0.06406,8.1687 -0.0644,8.1687 -0.02931,8.1687 0.0294,8.1686 0.06445,8.1687 0.064,8.1687 0.02805,8.1688 0.04143,0.3703 0.11803,0.3513 0.18477,0.3229 0.24169,0.2852 0.28879,0.2381 0.32606,0.1818 0.35351,0.1161 0.37114,0.0412 7.41336,0.0238 7.4133,0.0634 14.8262,0.1931 14.8259,0.1935 7.4129,0.0637 7.4128,0.0242 7.4129,-0.0242 7.4129,-0.0638 14.8259,-0.1935 14.8264,-0.1931 7.413,-0.0634 7.413,-0.0237 z';

          const currentVisibleKeycapIndex = visibleKeycapIndex;
          visibleKeycapIndex += 1; // spacers do not count as visible keycaps

          const onePointSevenFiveUnit = renderKeycapBorder(
            keycap,
            rowIndex,
            keycapIndex,
            visibleRowIndex,
            currentVisibleKeycapIndex,
            xPosForKeycap,
            yPosForRow,
            xOffset,
            yOffset,
            matrix,
            dPath
          );

          xPosForKeycap += keycap.width * 19.05;
          return onePointSevenFiveUnit;
        } else if (keycap.width === 1 && keycap.height === 2) {
          matrix = 'matrix(0,-0.26458805,-0.26458733,0,19.843912,39.423269)';
          dPath =
            'M 146.504,5.95573 146.386,5.60386 146.202,5.28024 145.96,4.99432 145.672,4.75553 145.346,4.57329 144.993,4.45704 144.621,4.416495 136.167,4.39293 127.713,4.32906 110.806,4.13446 93.8985,3.93956 85.4449,3.87531 76.9913,3.85095 68.5377,3.87536 60.0841,3.93965 43.1768,4.1346 26.2692,4.32918 17.8153,4.39301 9.36123,4.416535 8.99248,4.45991 8.64195,4.57796 8.31917,4.76111 8.0337,5.00001 7.79507,5.28535 7.61283,5.60778 7.49651,5.95796 7.456035,6.32655 7.42792,14.495 7.36338,22.6634 7.29848,30.8317 7.26892,39 l 0.02956,8.1683 0.0649,8.1683 0.06454,8.1684 0.028115,8.1684 0.040985,0.3707 0.11771,0.3518 0.18432,0.3237 0.24118,0.2859 0.2883,0.2389 0.32567,0.1823 0.3533,0.1162 0.37118,0.0406 8.45322,0.0235 8.4531,0.0638 16.906,0.1946 16.9056,0.1949 8.4528,0.0643 8.4528,0.0245 8.4528,-0.0244 8.4527,-0.0643 16.9053,-0.1949 16.906,-0.1946 8.453,-0.0638 8.454,-0.0236 0.37,-0.0412 0.353,-0.1167 0.325,-0.1825 0.288,-0.2388 0.241,-0.2858 0.184,-0.3233 0.117,-0.3514 0.0405,-0.3702 0.0275,-8.1685 0.065,-8.1684 0.065,-8.1684 0.029,-8.1683 -0.029,-8.1683 -0.065,-8.1684 -0.065,-8.1684 -0.0275,-8.16846 z';

          const currentVisibleKeycapIndex = visibleKeycapIndex;
          visibleKeycapIndex += 1; // spacers do not count as visible keycaps

          const twoUnitVertical = renderKeycapBorder(
            keycap,
            rowIndex,
            keycapIndex,
            visibleRowIndex,
            currentVisibleKeycapIndex,
            xPosForKeycap,
            yPosForRow,
            xOffset,
            yOffset,
            matrix,
            dPath
          );

          xPosForKeycap += keycap.width * 19.05;
          return twoUnitVertical;
        } else if (keycap.width === 2.25 && keycap.height === 1) {
          matrix = 'matrix(-0.26458954,0,0,0.26461959,44.58277,-0.79516704)';
          dPath =
            'm 166.452,72.0373 -0.118,0.3528 -0.183,0.3249 -0.24,0.2872 -0.288,0.24 -0.325,0.1833 -0.353,0.117 -0.372,0.0411 -9.634,0.0225 -9.634,0.0628 -19.269,0.1932 -19.268,0.1942 -9.634,0.0641 L 87.5,74.1448 77.866,74.1204 68.2319,74.0563 48.9637,73.8621 29.6953,73.6689 20.061,73.6061 10.4265,73.5836 10.0552,73.5425 9.7021,73.4255 9.37699,73.2422 9.08944,73.0022 8.84907,72.7149 8.66551,72.3901 8.54838,72.0372 8.50729,71.6659 8.32051,39.0209 8.50729,6.334045 8.54839,5.9627 8.66553,5.60986 8.8491,5.28503 9.08946,4.99781 9.37701,4.75776 9.70212,4.57446 10.0552,4.45749 10.4265,4.41643 20.061,4.39393 29.6953,4.33112 48.9637,4.13794 68.2319,3.9437 77.866,3.87957 87.5,3.85522 l 9.634,0.02434 9.634,0.06412 19.268,0.19423 19.269,0.19318 9.634,0.06282 9.634,0.02252 0.372,0.04106 0.353,0.11698 0.325,0.18331 0.288,0.24006 0.24,0.28724 0.183,0.32484 0.118,0.35286 0.041,0.37131 0.186,32.64501 -0.186,32.68685 z';

          const currentVisibleKeycapIndex = visibleKeycapIndex;
          visibleKeycapIndex += 1; // spacers do not count as visible keycaps

          const twoPointTwoFiveUnit = renderKeycapBorder(
            keycap,
            rowIndex,
            keycapIndex,
            visibleRowIndex,
            currentVisibleKeycapIndex,
            xPosForKeycap,
            yPosForRow,
            xOffset,
            yOffset,
            matrix,
            dPath
          );

          xPosForKeycap += keycap.width * 19.05;
          return twoPointTwoFiveUnit;
        } else if (keycap.width === 2.75 && keycap.height === 1) {
          matrix = 'matrix(-0.26458808,0,0,0.26461662,54.240271,-0.79505253)';
          dPath =
            'm 200.445,4.45709 0.354,0.11605 0.327,0.18198 0.289,0.23853 0.242,0.2857 0.185,0.3235 0.118,0.35191 0.042,0.37096 0.185,32.67428 -0.185,32.6743 -0.042,0.3709 -0.118,0.352 -0.185,0.3234 -0.242,0.2857 -0.289,0.2386 -0.327,0.182 -0.354,0.116 -0.372,0.0408 -23.518,0.087 -23.518,0.1931 -23.519,0.1934 L 106,74.1452 82.4812,74.0571 58.9628,73.8636 35.4444,73.6705 11.9256,73.5837 11.554,73.5429 11.2001,73.4268 10.8738,73.2447 10.5848,73.0062 10.3429,72.7204 10.1581,72.3969 10.04,72.045 9.99848,71.6742 9.8125,39 9.99848,6.32585 10.04,5.95498 10.1581,5.6031 10.3429,5.27959 10.5848,4.99385 10.8738,4.75526 11.2001,4.57321 11.554,4.45711 11.9256,4.41634 35.4444,4.32948 58.9628,4.13638 82.4812,3.94289 106,3.85483 l 23.518,0.08793 23.519,0.19341 23.518,0.19311 23.518,0.08705 z';

          const currentVisibleKeycapIndex = visibleKeycapIndex;
          visibleKeycapIndex += 1; // spacers do not count as visible keycaps

          const twoPointSevenFiveUnit = renderKeycapBorder(
            keycap,
            rowIndex,
            keycapIndex,
            visibleRowIndex,
            currentVisibleKeycapIndex,
            xPosForKeycap,
            yPosForRow,
            xOffset,
            yOffset,
            matrix,
            dPath
          );

          xPosForKeycap += keycap.width * 19.05;
          return twoPointSevenFiveUnit;
        } else if (keycap.width === 2 && keycap.height === 1) {
          matrix = 'matrix(-0.26458805,0,0,0.26458733,39.423269,-0.79391287)';
          dPath =
            'M 146.504,5.95573 146.386,5.60386 146.202,5.28024 145.96,4.99432 145.672,4.75553 145.346,4.57329 144.993,4.45704 144.621,4.416495 136.167,4.39293 127.713,4.32906 110.806,4.13446 93.8985,3.93956 85.4449,3.87531 76.9913,3.85095 68.5377,3.87536 60.0841,3.93965 43.1768,4.1346 26.2692,4.32918 17.8153,4.39301 9.36123,4.416535 8.99248,4.45991 8.64195,4.57796 8.31917,4.76111 8.0337,5.00001 7.79507,5.28535 7.61283,5.60778 7.49651,5.95796 7.456035,6.32655 7.42792,14.495 7.36338,22.6634 7.29848,30.8317 7.26892,39 l 0.02956,8.1683 0.0649,8.1683 0.06454,8.1684 0.028115,8.1684 0.040985,0.3707 0.11771,0.3518 0.18432,0.3237 0.24118,0.2859 0.2883,0.2389 0.32567,0.1823 0.3533,0.1162 0.37118,0.0406 8.45322,0.0235 8.4531,0.0638 16.906,0.1946 16.9056,0.1949 8.4528,0.0643 8.4528,0.0245 8.4528,-0.0244 8.4527,-0.0643 16.9053,-0.1949 16.906,-0.1946 8.453,-0.0638 8.454,-0.0236 0.37,-0.0412 0.353,-0.1167 0.325,-0.1825 0.288,-0.2388 0.241,-0.2858 0.184,-0.3233 0.117,-0.3514 0.0405,-0.3702 0.0275,-8.1685 0.065,-8.1684 0.065,-8.1684 0.029,-8.1683 -0.029,-8.1683 -0.065,-8.1684 -0.065,-8.1684 -0.0275,-8.16846 z';

          const currentVisibleKeycapIndex = visibleKeycapIndex;
          visibleKeycapIndex += 1; // spacers do not count as visible keycaps

          const twoUnit = renderKeycapBorder(
            keycap,
            rowIndex,
            keycapIndex,
            visibleRowIndex,
            currentVisibleKeycapIndex,
            xPosForKeycap,
            yPosForRow,
            xOffset,
            yOffset,
            matrix,
            dPath
          );

          xPosForKeycap += keycap.width * 19.05;
          return twoUnit;
        }
      } else {
        // Update xPosForKeycap or yPosForRow based on the spacer's dimensions
        xPosForKeycap += keycap.width * 19.05;
        totalVerticalSpacing += keycap.height * 19.05; // Vertical spacer adjustment
        return null;
      }
    });
  });
};

export default KeycapBaseLayer;
